<script setup lang="ts">
// Vendor
import { type Document } from "@contentful/rich-text-types";

// Types
import type {
  IMembershipSubscription,
  TMembershipCompareCallToAction,
} from "@citizenm/membership/shared/types/comparesubscriptions";

// TODO: add CTA support when available in Contentful
const { callToAction, dark, introduction, title, badge, subscriptions } =
  defineProps<{
    callToAction?: TMembershipCompareCallToAction | null;
    dark?: boolean;
    introduction?: Document;
    badge?: string;
    subscriptions?: IMembershipSubscription[];
    title: string;
  }>();

const columnsCount = computed(() => {
  const count = Array.isArray(subscriptions) ? subscriptions.length : 0;

  if (count <= 1) {
    return Math.max(count, 1);
  }

  // The amount of columns is the amount of subscriptions + a divider for each subscription but the last
  return count + (count - 1);
});
</script>

<template>
  <div
    :class="[
      'membership-compare-subscriptions',
      `membership-compare-subscriptions--${columnsCount}-columns`,
      { 'membership-compare-subscriptions--dark': dark },
    ]"
  >
    <div class="container">
      <div>
        <FoundationBadge class="membership-compare-subscriptions__badge">
          {{ badge }}
        </FoundationBadge>
        <h2 class="text-3xl">{{ title }}</h2>
        <div class="membership-compare-subscriptions__introduction">
          <ContentfulNodeRenderer
            v-if="introduction?.content"
            :nodes="introduction.content"
          />
        </div>
      </div>
      <div v-if="subscriptions" class="membership-compare-subscriptions__items">
        <template v-for="(subscription, index) in subscriptions" :key="index">
          <MembershipCompareSubscription
            :logo="subscription.logo"
            :description="subscription.description"
            :features="subscription.features"
          />
          <div
            v-if="index < subscriptions.length - 1"
            class="membership-compare-subscriptions__divider"
          >
            <span>+</span>
          </div>
        </template>
      </div>
      <div v-if="callToAction" class="membership-compare-subscriptions__cta">
        <FoundationBaseButton :to="callToAction.ctaUrl">
          {{ callToAction.ctaLabel }}
        </FoundationBaseButton>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.membership-compare-subscriptions {
  container-type: inline-size;
  padding-block: var(--spacing-20, 5rem);
  background-color: var(--cm-membership-compare-subscriptions-background, #fff);

  &__badge {
    color: var(--cm-color-white, #fff);
    background: var(--cm-membership-bonvoy-plus-gradient, #8c6f2b);
    border: none;
    margin-block: var(--spacing-1, 0.25rem);
  }

  &__cta {
    display: flex;
    margin-block-start: var(--spacing-4, 1rem);

    @container (width > 40rem) {
      justify-content: flex-end;
      margin-block-start: var(--spacing-6, 1.5rem);
    }

    @container (width > 75rem) {
      margin-block-start: var(--spacing-8, 2rem);
    }

    button,
    a {
      flex: 1 1 auto;

      @container (width > 40rem) {
        flex: 0 0 auto;
      }
    }
  }

  &__divider {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    justify-content: center;

    /* Rendering our own font is causing issues with line height. This + sign looks exactly the same as the one in the design */
    font-family: Arial, Helvetica, sans-serif;

    span {
      display: inline-block;
      font-size: var(--cm-membership-compare-divider-font-size, 5rem);
      line-height: 1;
      background: var(--cm-membership-bonvoy-plus-gradient, #8c6f2b);
      background-clip: text;
      -webkit-text-fill-color: transparent;
    }
  }

  &__introduction {
    max-width: 85ch;
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4, 1rem);
    padding: var(--spacing-6, 1.5rem);
    background:
      linear-gradient(var(--cm-color-white, #fff), var(--cm-color-white, #fff))
        padding-box,
      var(--cm-membership-bonvoy-plus-gradient, #8c6f2b) border-box;
    border: 2px solid transparent;
    border-radius: 14px;
    margin-block-start: var(--spacing-8, 2rem);

    @container (width > 55rem) {
      flex-direction: row;
      gap: var(--spacing-6, 1.5rem);
      padding: var(--spacing-10, 2.5rem);
      border-width: 3px;
      border-radius: 20px;
    }

    @container (width > 80rem) {
      gap: var(--spacing-8, 2rem);
      padding: var(--spacing-14, 3.5rem);
    }
  }

  /* Variants */
  &--dark {
    --cm-membership-compare-subscriptions-background: var(
      --cm-color-black,
      #000
    );

    color: var(--cm-color-white, #fff);
  }

  &--dark &__items {
    background:
      linear-gradient(
          var(--cm-membership-compare-subscriptions-background),
          var(--cm-membership-compare-subscriptions-background)
        )
        padding-box,
      var(--cm-membership-bonvoy-plus-gradient, #8c6f2b) border-box;
  }
}
</style>
